import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../../../store/hooks.ts';
import {
  selectDoraFinancialEntities,
  setDoraFinancialEntities,
} from '../store/financialEntity/financialEntitySlice.ts';
import {
  selectDoraICTThirdPartyProviders,
  setDoraICTThirdPartyProviders,
} from '../store/ictThirdPartyProvider/ictThirdPartyProviderSlice.ts';
import {
  addDoraICTService,
  removeDoraICTService,
  selectDoraICTServices,
  setDoraICTServices,
  updateDoraICTService as updateDoraICTServiceState,
} from '../store/ictService/ictServiceSlice.ts';
import {
  createDoraICTService,
  deleteDoraICTService,
  listDoraFinancialEntities,
  listDoraICTServices,
  listDoraICTThirdPartyProviders,
  updateDoraICTService,
} from '../../../api.ts';
import { DoraICTService, doraICTServiceToUpdate } from '../models/doraICTService.ts';
import { selectCurrentOrganization } from '../../../store/organization/organizationSlice.ts';

export const ICTServicesPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const financialEntities = useAppSelector(selectDoraFinancialEntities);
  const thirdPartyProviders = useAppSelector(selectDoraICTThirdPartyProviders);
  const ictServices = useAppSelector(selectDoraICTServices);
  const currentOrganization = useAppSelector(selectCurrentOrganization);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isExternal, setIsExternal] = useState(false);
  const [financialEntityId, setFinancialEntityId] = useState<number | ''>('');
  const [thirdPartyId, setThirdPartyId] = useState<number | ''>('');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editIsExternal, setEditIsExternal] = useState(false);
  const [editFinancialEntityId, setEditFinancialEntityId] = useState<number | ''>('');
  const [editThirdPartyId, setEditThirdPartyId] = useState<number | ''>('');

  const sortedServices = useMemo(
    () => [...ictServices].sort((a, b) => a.name.localeCompare(b.name)),
    [ictServices],
  );

  useEffect(() => {
    if (!financialEntityId && financialEntities.length > 0) {
      setFinancialEntityId(financialEntities[0].id ?? '');
    }
    if (!editFinancialEntityId && financialEntities.length > 0 && editingId) {
      setEditFinancialEntityId(financialEntities[0].id ?? '');
    }
  }, [financialEntities, financialEntityId, editFinancialEntityId, editingId]);

  const createDisabled = !name.trim() || !financialEntityId;

  useEffect(() => {
    const load = async () => {
      if (!currentOrganization) return;
      setLoading(true);
      setError(null);
      try {
        const [fes, providers, services] = await Promise.all([
          listDoraFinancialEntities(),
          listDoraICTThirdPartyProviders(),
          listDoraICTServices(),
        ]);
        dispatch(setDoraFinancialEntities(fes));
        dispatch(setDoraICTThirdPartyProviders(providers));
        dispatch(setDoraICTServices(services));
      } catch (err: any) {
        console.error('Failed to load ICT services', err);
        setError(err?.response?.data?.detail || 'Failed to load ICT services');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentOrganization, dispatch]);

  const handleCreate = async () => {
    if (!name.trim() || !financialEntityId) {
      setError('Name and Financial Entity are required');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await createDoraICTService({
        name: name.trim(),
        description: description || null,
        is_external: isExternal,
        financial_entity_id: Number(financialEntityId),
        third_party_provider_id: thirdPartyId === '' ? null : Number(thirdPartyId),
      });
      dispatch(addDoraICTService(created));
      setName('');
      setDescription('');
      setIsExternal(false);
      setFinancialEntityId('');
      setThirdPartyId('');
      setSuccess('ICT service created');
    } catch (err: any) {
      console.error('Failed to create ICT service', err);
      setError(err?.response?.data?.detail || 'Failed to create ICT service');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (service: DoraICTService) => {
    setEditingId(service.id ?? null);
    setEditName(service.name);
    setEditDescription(service.description || '');
    setEditIsExternal(service.is_external);
    setEditFinancialEntityId(service.financial_entity_id);
    setEditThirdPartyId(service.third_party_provider_id ?? '');
    setError(null);
    setSuccess(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditDescription('');
    setEditIsExternal(false);
    setEditFinancialEntityId('');
    setEditThirdPartyId('');
  };

  const handleUpdate = async (service: DoraICTService) => {
    if (!service.id) return;
    if (!editName.trim() || !editFinancialEntityId) {
      setError('Name and Financial Entity are required');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = doraICTServiceToUpdate({
        ...service,
        name: editName.trim(),
        description: editDescription || null,
        is_external: editIsExternal,
        financial_entity_id: Number(editFinancialEntityId),
        third_party_provider_id: editThirdPartyId === '' ? null : Number(editThirdPartyId),
      });
      const updated = await updateDoraICTService(service.id, payload);
      dispatch(updateDoraICTServiceState(updated));
      cancelEdit();
      setSuccess('ICT service updated');
    } catch (err: any) {
      console.error('Failed to update ICT service', err);
      setError(err?.response?.data?.detail || 'Failed to update ICT service');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id?: number) => {
    if (!id) return;
    if (!window.confirm('Delete this ICT service?')) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await deleteDoraICTService(id);
      dispatch(removeDoraICTService(id));
      setSuccess('ICT service deleted');
    } catch (err: any) {
      console.error('Failed to delete ICT service', err);
      setError(err?.response?.data?.detail || 'Failed to delete ICT service');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Add ICT Service
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="ict-financial-entity-label">Financial Entity</InputLabel>
              <Select
                labelId="ict-financial-entity-label"
                value={financialEntityId === '' ? '' : String(financialEntityId)}
                label="Financial Entity"
                onChange={(e: SelectChangeEvent<string>) => setFinancialEntityId(Number(e.target.value))}
              >
                {financialEntities.map(fe => (
                  <MenuItem key={fe.id} value={fe.id}>{fe.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="ict-third-party-label" shrink>Third-Party Provider</InputLabel>
              <Select
                labelId="ict-third-party-label"
                value={thirdPartyId === '' ? '' : String(thirdPartyId)}
                label="Third-Party Provider"
                onChange={(e: SelectChangeEvent<string>) => setThirdPartyId(e.target.value ? Number(e.target.value) : '')}
                displayEmpty
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {thirdPartyProviders.map(tp => (
                  <MenuItem key={tp.id} value={tp.id}>{tp.name_latin}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isExternal}
                  onChange={(e) => setIsExternal(e.target.checked)}
                />
              }
              label="External Service"
            />
            <Button variant="contained" onClick={handleCreate} disabled={loading || createDisabled}>
              Create
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      {financialEntities.length === 0 && (
        <Alert severity="warning">
          No Financial Entities found. Please create financial entities in the <strong>Financial Entities</strong> panel (B_01.02) before creating ICT services.
        </Alert>
      )}

      {thirdPartyProviders.length === 0 && (
        <Alert severity="info">
          No ICT Third-Party Providers found. You can optionally create providers in the <strong>ICT Third-Party Providers</strong> panel (B_05.01) to link them to ICT services.
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">ICT Services</Typography>
            {loading && <CircularProgress size={20} />}
          </Stack>
          {sortedServices.length === 0 ? (
            <Alert severity="info">No ICT services yet. Add one above.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Financial Entity</TableCell>
                    <TableCell>Third-Party</TableCell>
                    <TableCell>External</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedServices.map(service => {
                    const isEditing = editingId === service.id;
                    const feName = financialEntities.find(fe => fe.id === service.financial_entity_id)?.name || '—';
                    const tpName = service.third_party_provider_id
                      ? (thirdPartyProviders.find(tp => tp.id === service.third_party_provider_id)?.name_latin || '—')
                      : 'None';
                    return (
                      <TableRow key={service.id ?? service.name}>
                        <TableCell width="20%">
                          {isEditing ? (
                            <TextField value={editName} onChange={(e) => setEditName(e.target.value)} size="small" fullWidth />
                          ) : service.name}
                        </TableCell>
                        <TableCell>
                          {isEditing ? (
                            <TextField value={editDescription} onChange={(e) => setEditDescription(e.target.value)} size="small" fullWidth />
                          ) : service.description || '—'}
                        </TableCell>
                        <TableCell width="20%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editFinancialEntityId === '' ? '' : String(editFinancialEntityId)}
                                onChange={(e: SelectChangeEvent<string>) => setEditFinancialEntityId(Number(e.target.value))}
                              >
                                {financialEntities.map(fe => (
                                  <MenuItem key={fe.id} value={fe.id}>{fe.name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : feName}
                        </TableCell>
                        <TableCell width="18%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editThirdPartyId === '' ? '' : String(editThirdPartyId)}
                                onChange={(e: SelectChangeEvent<string>) => setEditThirdPartyId(e.target.value ? Number(e.target.value) : '')}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {thirdPartyProviders.map(tp => (
                                  <MenuItem key={tp.id} value={tp.id}>{tp.name_latin}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : tpName}
                        </TableCell>
                        <TableCell width="10%">
                          {isEditing ? (
                            <Checkbox
                              checked={editIsExternal}
                              onChange={(e) => setEditIsExternal(e.target.checked)}
                            />
                          ) : (service.is_external ? 'Yes' : 'No')}
                        </TableCell>
                        <TableCell align="right">
                          {isEditing ? (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton onClick={() => handleUpdate(service)} size="small" disabled={loading}>
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton onClick={cancelEdit} size="small" disabled={loading}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ) : (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton onClick={() => startEdit(service)} size="small" disabled={loading}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton onClick={() => handleDelete(service.id)} size="small" disabled={loading}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};

export default ICTServicesPanel;
